import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
} from '@mui/material';
import { closeSnackbar, useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentReturnedOutlinedIcon from '@mui/icons-material/AssignmentReturnedOutlined';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { GridColDef } from '@mui/x-data-grid';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import { DefaultValues, useForm } from 'react-hook-form';
import { DataGrid } from '../MysteryBoxSummaryPage';
import { useParams } from 'react-router-dom';
import PublishedChip from '@/common/components/PublishedChip';
import { PUBLISHED_LISTA, PUBLISHED_LIST_ } from '@/common/constants/common';
import {
	useGetBoxSingleListQuery,
	useSetBoxSingleStatusMutation,
} from '@/api/exchange/boxApiSlice';
import { BoxSingleDto } from '@/api/DTO/exchange/box.interface';
import { OnoffType } from '@/api/enum/common.enum';

interface Props {}

export default function SetSingleStatusDialog(props: Props) {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [open, setOpen] = useState(false);
	const [setBoxSingleStatus] = useSetBoxSingleStatusMutation();
	const { boxId } = useParams();

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetBoxSingleListQuery(
			{
				blindBoxId: boxId,
				pagination: {
					page: 1,
					size: 99999,
				},
				sortings: [],
			},
			{
				refetchOnMountOrArgChange: true,
				skip: !open,
			}
		);

	const defaultValues: DefaultValues<any> = {};

	const methods = useForm<any>({
		defaultValues,
	});

	const onSubmit = async () => {
		const data: { id: string; published: number }[] = Object.entries(
			methods.getValues()
		).map(([key, value]) => {
			return {
				id: key,
				published: Number(value) > 0 ? 1 : 0,
			};
		});
		const response = await setBoxSingleStatus({
			blindBoxId: boxId || '',
			publishList: data,
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.changeStatusSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	const handleClose = () => {
		setOpen(false);
	};

	const columns: GridColDef[] = [
		{
			field: 'sort',
			headerName: t('achievementList.level'),
			flex: 25,
			renderCell: (params: any) => params.row.sort || '-',
		},
		{
			field: 'finishedNum',
			headerName: t('box.drawsTotal'),
			flex: 25,
		},
		{
			field: 'rewardPoints',
			headerName: t('box.rewardPoints'),
			flex: 25,
		},
		{
			field: 'rewardDesc',
			headerName: t('box.rewardTotal'),
			flex: 25,
		},
		{
			field: 'published',
			headerName: t('box.taskStatus'),
			flex: 25,
			renderCell: (params: any) => (
				<PublishedChip
					published={params.row.published}
					publishList={PUBLISHED_LISTA}
				/>
			),
		},
		{
			field: 'changePublished',
			headerName: t('meditation.onoff'),
			flex: 40,
			renderCell: (params: any) => (
				<AppRadioGroup
					control={methods.control}
					name={params.row.id}
					row
					options={PUBLISHED_LIST_}
				/>
			),
		},
	];

	const transformResponse = useCallback((response?: BoxSingleDto[]) => {
		if (!response) return;
		const newResponse: DataGrid[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
			});

			methods.setValue(item.id, item.published ? OnoffType.on : OnoffType.off);
		});

		return newResponse;
	}, []);

	const formContent = (
		<>
			<Button
				variant='contained'
				style={{ alignItems: 'center' }}
				endIcon={<AssignmentReturnedOutlinedIcon />}
				onClick={() => setOpen(true)}
			>
				{t('meditation.onoff')}
			</Button>
			<Dialog
				open={open}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='xl'
				fullWidth
			>
				<DialogTitle variant='h5'>{t('meditation.onoff')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent>
					<Grid container alignItems='stretch' columnSpacing={3} marginTop={3}>
						<Grid item xs display='flex'>
							<LocalDataGrid
								rows={transformResponse(propertySummary?.data.items) ?? []}
								columns={columns}
								rowCount={0}
								getRowId={(row) => row.id}
								pagination={false}
							/>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleClose()}>{t('button.cancel')}</Button>

					<Button type='submit' variant='contained' onClick={() => onSubmit()}>
						{t('button.save')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
	return <>{formContent}</>;
}
